/**
 * Cross-Sell job queue store.
 *
 * Generalisation of `useEnrichmentQueueStore` (shared/stores) for the cross-sell
 * global notification channel (UX_SPEC_F1_DELTA §5.3 / EDA §4). Same Zustand +
 * persist localStorage rules (MAX_ENTRY_AGE_MS=10min, MAX_ENTRIES=25,
 * onRehydrateStorage drops stale `processing` entries), but the domain is
 * bulk_import (+ future campaign jobs) and entries are keyed by `jobId`.
 *
 * Two population paths (EDA §4.2):
 *   1. Action-initiated — after POST /imports the wizard calls `enqueue`.
 *   2. SSE-initiated — the global stream (`/cross-sell/events/stream`, opened in
 *      AppLayout) drives `enqueue`/`markStatus` so a job started in ANOTHER tab
 *      shows up here too.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type CrossSellJobType = 'bulk_import' | 'campaign_activation' | 'whitespace_refresh';

/** Mirrors the import state machine plus a generic processing umbrella. */
export type CrossSellJobStatus =
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface CrossSellQueueEntry {
  /** Dedupe key — the import `jobId` (UUID). */
  jobId: string;
  type: CrossSellJobType;
  /** Human label, e.g. "base-junho.xlsx". */
  label: string;
  status: CrossSellJobStatus;
  /** 0–100 progress for in-flight imports (optional). */
  progress?: number;
  etaSeconds?: number | null;
  startedAt: number;
  completedAt?: number;
  /** Set true once the drawer row has been seen. */
  viewed: boolean;
  /** Guards against firing toast/desktop notification more than once. */
  alertEmitted: boolean;
  /** Outcome counters surfaced on completion (for the toast description). */
  accountsImported?: number;
  assetsImported?: number;
  decisoresImported?: number;
}

/** Stream connectivity health, drives the drawer "offline" warning card. */
export type CrossSellStreamMode = 'sse' | 'polling';

interface CrossSellQueueState {
  entries: CrossSellQueueEntry[];
  streamMode: CrossSellStreamMode;
  /** Epoch ms of the last event received on the global stream. */
  lastEventAt: number | null;

  enqueue: (
    entry: Omit<CrossSellQueueEntry, 'status' | 'viewed' | 'alertEmitted' | 'startedAt'> &
      Partial<Pick<CrossSellQueueEntry, 'status' | 'viewed' | 'alertEmitted' | 'startedAt'>>,
  ) => void;
  markStatus: (
    jobId: string,
    status: CrossSellJobStatus,
    extras?: {
      progress?: number;
      etaSeconds?: number | null;
      accountsImported?: number;
      assetsImported?: number;
      decisoresImported?: number;
    },
  ) => void;
  markAlertEmitted: (jobId: string) => void;
  markAllViewed: () => void;
  removeOnClick: (jobId: string) => void;
  clearOlderThan: (ageMs: number) => void;
  setStreamMode: (mode: CrossSellStreamMode) => void;
  noteEvent: () => void;
  clear: () => void;
}

const MAX_ENTRY_AGE_MS = 10 * 60 * 1000;
const MAX_ENTRIES = 25;

export const useCrossSellQueueStore = create<CrossSellQueueState>()(
  persist(
    (set, get) => ({
      entries: [],
      streamMode: 'sse',
      lastEventAt: null,

      enqueue: (entry) =>
        set((state) => {
          const existing = state.entries.find((e) => e.jobId === entry.jobId);
          if (existing) return state;
          const next: CrossSellQueueEntry = {
            status: 'processing',
            viewed: false,
            alertEmitted: false,
            startedAt: Date.now(),
            ...entry,
          };
          return { entries: [next, ...state.entries].slice(0, MAX_ENTRIES) };
        }),

      markStatus: (jobId, status, extras) =>
        set((state) => ({
          entries: state.entries.map((e) =>
            e.jobId === jobId
              ? {
                  ...e,
                  status,
                  completedAt: status !== 'processing' ? Date.now() : e.completedAt,
                  progress: extras?.progress ?? e.progress,
                  etaSeconds: extras?.etaSeconds ?? e.etaSeconds,
                  accountsImported: extras?.accountsImported ?? e.accountsImported,
                  assetsImported: extras?.assetsImported ?? e.assetsImported,
                  decisoresImported: extras?.decisoresImported ?? e.decisoresImported,
                }
              : e,
          ),
        })),

      markAlertEmitted: (jobId) =>
        set((state) => ({
          entries: state.entries.map((e) =>
            e.jobId === jobId ? { ...e, alertEmitted: true } : e,
          ),
        })),

      markAllViewed: () =>
        set((state) => ({
          entries: state.entries.map((e) => (e.viewed ? e : { ...e, viewed: true })),
        })),

      removeOnClick: (jobId) =>
        set((state) => ({
          entries: state.entries.filter((e) => e.jobId !== jobId),
        })),

      clearOlderThan: (ageMs) => {
        const now = Date.now();
        set((state) => ({
          entries: state.entries.filter((e) => {
            const ts = e.completedAt ?? e.startedAt;
            return now - ts < ageMs;
          }),
        }));
        void get();
      },

      setStreamMode: (mode) => set({ streamMode: mode }),
      noteEvent: () => set({ lastEventAt: Date.now() }),

      clear: () => set({ entries: [] }),
    }),
    {
      name: 'cross-sell-queue-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ entries: state.entries }),
      version: 1,
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const now = Date.now();
        state.entries = state.entries.filter((e) => {
          const ts = e.completedAt ?? e.startedAt;
          if (now - ts > MAX_ENTRY_AGE_MS) return false;
          if (e.status === 'processing' && now - e.startedAt > MAX_ENTRY_AGE_MS) {
            return false;
          }
          return true;
        });
      },
    },
  ),
);

/* ── Derived selectors ──────────────────────────────────────────────────── */

export const selectPendingCount = (s: CrossSellQueueState): number =>
  s.entries.reduce((acc, e) => (e.status === 'processing' ? acc + 1 : acc), 0);

export const selectUnviewedCompleted = (s: CrossSellQueueState): number =>
  s.entries.reduce(
    (acc, e) => (e.status !== 'processing' && !e.viewed ? acc + 1 : acc),
    0,
  );

export const selectTotalBadge = (s: CrossSellQueueState): number =>
  selectPendingCount(s) + selectUnviewedCompleted(s);

export { MAX_ENTRY_AGE_MS };
